import { AlmeAppUtilsBase, getCurrentAppState, updateAppState } from './components/AlmeApp/AlmeAppState';
import { ConverseRequestDetailsNext } from '@alme/api-ts/Alme/ConverseRequestDetails';
import { RequestUnitRequestDetailsNext } from '@alme/api-ts/Alme/RequestUnitRequestDetails';
import { HistoryRequestDetailsNext } from '@alme/api-ts/Alme/HistoryRequestDetails';
import { AppEventRequestDetailsNext } from '@alme/api-ts/Alme/AppEventRequestDetails';
import { ConfigurationRequestDetailsNext } from '@alme/api-ts/Alme/ConfigurationRequestDetails';
import { AlmeSessionDataUtils } from '@alme/session-ts/AlmeSessionDataUtils';
import deepmerge from 'deepmerge';
import { LiveChatNotificationRequestDetailsNext } from '@alme/api-ts/LiveChat/LiveChatNotificationRequestDetails';
import { ReportingEventRequestDetailsNext } from '@alme/api-ts/Alme/ReportingEventRequestDetails';
import MessageType from './post-message/MessageType';
import { getAccountId } from './components/AlmeApp/initialize/loginState';
import postMessage from './post-message/PostMessageUtils';
import { AlmeMinimizeUtils } from './components/AlmeHeader/AlmeMinimize/AlmeMinimize';
import { updateOperatorNameInBeginLiveChatResponse, updateOperatorNameInSessionEndResponse, updateNameAsNotEngagedInSessionEndResponse, updateSessionEndResponseWhenTimeout } from './components/LiveChat/AlmeLiveChatResponse/UpdateChatResponses';
import { EndConversationRequestDetailsNext } from '@alme/api-ts/Alme/EndConversationRequestDetails';
import { getTenantByAccessKey, defaultTenant } from './components/AlmeTenantSelector/AlmeTenants';
import { generatePlatformValue, getSessionLoader, isCCTRLoggedIn } from './platform/identifiers';
import { getWebLogAPIUrl } from './utils/UrlUtils';
import { getTenantName } from './components/AlmeUtilities/Alme-utils';
const sessionDataLoader = getSessionLoader();
const ResponseActionName = "SwitchAccountConfirmation";
const ConsumerCenterNavigationLinkRegex = /^\{(.*)\}$/;
export class AlmeApiUtils extends AlmeAppUtilsBase {
    constructor(almeState, setAlmeState, appConfig, almeApi, liveChatNotificationApi) {
        super(almeState, setAlmeState, appConfig);
        let almeSessionData;
        try {
            almeSessionData = sessionDataLoader.GetAlmeSessionData();
        }
        catch (ex) {
            this.logger.error(ex);
        }
        if (!almeSessionData) {
            almeSessionData = AlmeSessionDataUtils.CreateAlmeSessionData();
        }
        this.almeSession = almeSessionData;
        this.almeApi = almeApi;
        this.liveChatNotificationApi = liveChatNotificationApi;
        this.almeMinimizeUtils = new AlmeMinimizeUtils(this.almeState, this.setAlmeState, this.appConfig);
    }
    getErrorResponse() {
        const responseRevision = this.almeState.history.length + 1;
        return {
            id: this.appConfig.history.errorResponse.entryId,
            userId: this.almeSession.userId,
            text: this.appConfig.SystemErrorResponse,
            responseRevision: responseRevision,
            displayLinkCollection: {
                Sections: [],
                Metadata: null,
            },
            navUrl: null,
            responseActions: [],
            deferredAppCall: null,
            maskedInput: null,
            isReverted: false,
            extensionData: {
                entryId: this.appConfig.history.errorResponse.entryId,
                chatId: "",
                MessageIndex: -1,
            },
        };
    }
    /**
     * Create Page Awareness parameter object and
     * deep merge with existing parameters.
     * @param parameters Current input parameters
     * @returns Input parameters merged with page awareness parameters
     */
    addPageAwareness(parameters) {
        var _a;
        const appState = getCurrentAppState();
        const loggedInState = { IsLoggedIn: appState === null || appState === void 0 ? void 0 : appState.isLoggedIn, ClientSessionId: appState === null || appState === void 0 ? void 0 : appState.clientSessionId };
        const context = this.createPageAwarenessObject(window.location);
        let platform = (_a = this.appConfig.main) === null || _a === void 0 ? void 0 : _a.platform;
        if (platform === "PSCC") {
            platform = (isCCTRLoggedIn()) ? "CCtr" : "CCtr IDP";
        }
        if (platform === "RC") {
            platform = generatePlatformValue(platform);
        }
        const apiParameters = Object.assign(Object.assign({}, parameters), { platform });
        const apiParams = deepmerge.all([context, loggedInState, apiParameters]);
        return apiParams;
    }
    addRequestParams(parameters) {
        const apiParameters = this.addPageAwareness(parameters);
        const UiVersion = this.appConfig.uiVersion;
        const jsSessionId = this.almeSession.sessionId;
        const text = (this.almeState.history.length > 0) ? this.almeState.history[this.almeState.history.length - 1].text : "";
        return Object.assign(Object.assign({}, apiParameters), { UiVersion,
            jsSessionId,
            text });
    }
    /**
     * Create Page Awareness parameter object.
     * @param location DOM window location object
     */
    createPageAwarenessObject(location) {
        const CurrentUrl = {
            AbsolutePath: location.href,
            Protocol: location.protocol,
            Host: location.host,
            HostName: location.hostname,
            Port: location.port,
            Uri: location.pathname,
            Query: location.search,
            Fragment: location.hash,
            Origin: location.origin,
        };
        return { Context: { CurrentUrl } };
    }
    sendErrorResponse() {
        const errorResponse = this.getErrorResponse();
        this.appendToHistory(errorResponse);
        this.validateAlmeSessionData(errorResponse);
    }
    /**
     * Submits the users input to alme using the Typescript Alme API.
     * @see AlmeApi.SendInputAsync
     * @see ConverseRequestDetails
     * @see [Alme Api Converse Request](https://confluence.nextit.com/pages/viewpage.action?pageId=35688489)
     * @see [Understanding Entry Types and Origins](https://confluence.nextit.com/display/AlmeTech/Understanding+Entry+Types+and+Origins)
     */
    async sendAlmeInput(input, origin = 'Typed', displayText = input, parameters = {}) {
        Object.assign(parameters, { AlmeUserId_FromUI: this.almeSession.userId }, { NotInLiveChat: !this.almeState.isLiveChatConnected });
        const requestParams = this.addRequestParams(parameters);
        const accessKey = this.almeState.accessKey;
        const request = new ConverseRequestDetailsNext(this.almeSession.userId, this.almeSession.sessionId, input, origin, displayText, requestParams, this.appConfig.channel, undefined, undefined, accessKey);
        const response = await this.almeApi.SendInputAsync(request);
        this.validateAlmeSessionData(response);
        return response;
    }
    /**
     * Submits the users input to alme using the Typescript Alme API and appends to history.
     * @see AlmeApi.SendInputAsync
     * @see ConverseRequestDetails
     * @see [Alme Api Converse Request](https://confluence.nextit.com/pages/viewpage.action?pageId=35688489)
     * @see [Understanding Entry Types and Origins](https://confluence.nextit.com/display/AlmeTech/Understanding+Entry+Types+and+Origins)
     */
    async submitAlmeInput(input, origin = 'Typed', displayText = input, parameters = {}) {
        try {
            const response = await this.sendAlmeInput(input, origin, displayText, parameters);
            this.appendToHistory(response);
            this.validateAlmeSessionData(response);
        }
        catch (error) {
            this.logger.error(error);
            this.sendErrorResponse();
        }
    }
    /**
     * Submits a Request Unit request
     * @see IUnitDisplayLink
     */
    async submitUnitRequest(unitLink, parameters = {}) {
        const apiParams = this.addRequestParams(parameters);
        const accessKey = this.almeState.accessKey;
        const unitRequest = new RequestUnitRequestDetailsNext(this.almeSession.userId, this.almeSession.sessionId, unitLink.Metadata.UnitUID, unitLink.DisplayText, 'Click', apiParams, undefined, this.appConfig.channel, undefined, accessKey);
        try {
            if (unitRequest.unitId === "2888ddae-7d2d-4a7c-ac5a-3d8eae4e689f" || unitRequest.unitId === "ceb9d86d-cacc-43bb-b0b6-01b62adf5fed") {
                this.almeState.wasSpanishLiveChatLinkClicked = true;
                updateAppState(this.almeState);
            }
            const response = await this.almeApi.SendRequestUnitAsync(unitRequest);
            this.processSendAccountEligibilityResponse(response);
            this.appendToHistory(response);
            this.validateAlmeSessionData(response);
        }
        catch (error) {
            this.logger.error(error);
            this.sendErrorResponse();
        }
    }
    /**
     * Sends an Alme GetHistory request and returns the history response or empty IAlmeResponseNext[] if no history exists.
     * @see AlmeApi.GetHistoryAsync
     * @see https://confluence.nextit.com/pages/viewpage.action?pageId=35688491
     */
    async loadAlmeHistory() {
        const historyRequest = new HistoryRequestDetailsNext(this.almeSession.sessionId);
        try {
            const responses = await this.almeApi.GetHistoryAsync(historyRequest);
            this.validateAlmeSessionData(responses);
            return responses;
        }
        catch (error) {
            this.logger.error(error);
            return [];
        }
    }
    validateEnterpriseId(enterpriseId, elemententerpriseId) {
        return enterpriseId === elemententerpriseId;
    }
    /**
     * Sends an Alme Application Event request.
     * @see https://confluence.nextit.com/pages/viewpage.action?pageId=35688488
     * @see https://confluence.nextit.com/display/AlmeTech/Understanding+Entry+Types+and+Origins
     */
    async sendAppEvent(eventName, origin = 'Internal', parameters = {}) {
        const apiParams = this.addRequestParams(parameters);
        const platform = this.appConfig.main.platform;
        let _accessKey;
        if (platform === "VISTA" && eventName !== "SwitchAccountExecute") {
            this.checkForAccessKey();
            _accessKey = this.almeState.accessKey;
        }
        else if (platform === "VISTA" && eventName === "SwitchAccountExecute") {
            const _platform = this.getPlatform(parameters);
            _accessKey = getTenantName(_platform).value;
        }
        else {
            _accessKey = this.almeState.accessKey;
        }
        const accessKey = _accessKey;
        if (eventName === "TransmitResult" && this.appConfig.IsEnableUILog === "True") {
            await this.TransmitWebLogger(eventName, origin, accessKey, apiParams);
        }
        const appEventRequest = new AppEventRequestDetailsNext(this.almeSession.userId, this.almeSession.sessionId, eventName, origin, apiParams, this.appConfig.channel, undefined, undefined, accessKey);
        try {
            const response = await this.almeApi.SendAppEventAsync(appEventRequest);
            this.validateAlmeSessionData(response);
            return response;
        }
        catch (error) {
            this.logger.error(error);
            return this.getErrorResponse();
        }
    }
    getPlatform(parameters) {
        var _a;
        const userMetaData = this.appConfig.main.userMetadata;
        const _currentEnterpriseId = (_a = parameters.currentEnterpriseId) !== null && _a !== void 0 ? _a : "";
        return userMetaData.platformInformation.find(x => x.enterpriseId === (_currentEnterpriseId || userMetaData.currentEnterpriseId));
    }
    checkForAccessKey() {
        var _a;
        const userMetaData = this.appConfig.main.userMetadata;
        const platform = userMetaData.platformInformation.find(x => x.enterpriseId === (this.almeState.currentEnterpriseId || userMetaData.currentEnterpriseId));
        const selectedAccesskey = getTenantName(platform).value;
        if (selectedAccesskey !== this.almeState.accessKey && this.appConfig.setTenant) {
            (_a = this.appConfig) === null || _a === void 0 ? void 0 : _a.setTenant(getTenantByAccessKey(selectedAccesskey) || defaultTenant);
            this.almeState.accessKey = selectedAccesskey;
            this.setAlmeState(Object.assign({}, this.almeState));
        }
    }
    switchAccountResponse(response) {
        if (Array.isArray(response === null || response === void 0 ? void 0 : response.responseActions) && !!(response === null || response === void 0 ? void 0 : response.responseActions.length)) {
            for (const action of response.responseActions) {
                if (action.Name === ResponseActionName) {
                    return action;
                }
            }
        }
    }
    processSendAccountEligibilityResponse(response) {
        const switchEventEligibilityResponse = this.switchAccountResponse(response);
        if ((switchEventEligibilityResponse === null || switchEventEligibilityResponse === void 0 ? void 0 : switchEventEligibilityResponse.Name) === ResponseActionName) {
            const userMetaData = this.appConfig.main.userMetadata;
            userMetaData.platformInformation.forEach(element => {
                var _a;
                if (this.validateEnterpriseId(switchEventEligibilityResponse === null || switchEventEligibilityResponse === void 0 ? void 0 : switchEventEligibilityResponse.enterpriseId, element.enterpriseId) && this.appConfig.setTenant) {
                    const selectedAccesskey = getTenantName(element).value;
                    (_a = this.appConfig) === null || _a === void 0 ? void 0 : _a.setTenant(getTenantByAccessKey(selectedAccesskey) || defaultTenant);
                    this.almeState.accessKey = selectedAccesskey;
                    this.setAlmeState(Object.assign({}, this.almeState));
                }
            });
        }
    }
    /**
     * submit an Alme Application Event request.
     * @see https://confluence.nextit.com/pages/viewpage.action?pageId=35688488
     * @see https://confluence.nextit.com/display/AlmeTech/Understanding+Entry+Types+and+Origins
     */
    async submitFormAppEvent(eventName, parameters = {}, origin = 'Internal') {
        const response = await this.sendAppEvent(eventName, origin, parameters);
        this.appendToHistory(response);
    }
    /**
     * Submits an Alme ReportingEvent and returns the success/failure as a boolean
     * @see ReportingEventRequestDetailsNext
     * @param variableName reporting event variable name
     * @param variableValue reporting event variable value
     */
    async submitReportingEvent(variableName, variableValue) {
        const request = new ReportingEventRequestDetailsNext(this.almeSession.userId, this.almeSession.sessionId, variableName, variableValue || '', this.appConfig.channel, undefined, this.almeState.accessKey);
        try {
            return await this.almeApi.SendReportingEventAsync(request);
        }
        catch (error) {
            this.logger.error(error);
            return null;
        }
    }
    /**
     * Sends an Alme GetConfiguration request.
     * @see https://confluence.nextit.com/pages/viewpage.action?pageId=35688487
     */
    async getAlmeConfiguration() {
        const configRequest = new ConfigurationRequestDetailsNext(this.appConfig.channel);
        try {
            return await this.almeApi.GetConfigurationAsync(configRequest);
        }
        catch (error) {
            this.logger.error(error);
            return null;
        }
    }
    /**
     * Send reporting event like UserRefreshedSessionTimeout
     * @param variableName
     * @param variableValue
     * @param userData
     */
    async sendReportingEvent(variableName, variableValue, userData) {
        const reportingEventParams = {
            VariableName: variableName,
            VariableValue: variableValue,
            channel: this.appConfig.channel,
            sessionId: this.almeSession.sessionId,
            userId: this.almeSession.userId,
            accessKey: this.almeState.accessKey,
            userData,
        };
        try {
            await this.almeApi.SendReportingEventAsync(reportingEventParams);
            this.setAlmeState(Object.assign(Object.assign({}, this.almeState), { lastActivityTime: Date.now() }));
        }
        catch (error) {
            this.logger.error(error);
        }
    }
    onDisplayUrlLinkClick(link, appConfig) {
        switch (link.Metadata.NavigationType) {
            case 'MainPage':
                this.urlNavigation(link.Metadata.UrlAddress, '_self', appConfig);
                break;
            case 'None':
                break;
            case 'Popup':
            default:
                this.urlNavigation(link.Metadata.UrlAddress, '_blank', appConfig);
                break;
        }
    }
    /**
     * Handles Alme DisplayLink click.
     * Link Types:
     *  - Url link will navigate
     *  - Input link will submitAlmeInput with the 'Click' origin
     *  - Unit link will submit a unit request
     * @see https://confluence.nextit.com/pages/viewpage.action?pageId=35688489 displayLinkCollection output property.
     */
    onDisplayLinkClick(link, appConfig) {
        switch (link.TargetType) {
            case 'Unit':
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                this.submitUnitRequest(link);
                break;
            case 'Url':
                this.onDisplayUrlLinkClick(link, appConfig);
                break;
            case 'Input':
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                this.submitAlmeInput(link.Metadata.InputText, 'Click', link.DisplayText);
                break;
            default:
                this.logger.warn('Unknown Link Type', link.TargetType);
                break;
        }
    }
    clearSessionData() {
        try {
            sessionDataLoader.ClearAlmeSessionData();
        }
        catch (ex) {
            this.logger.error(ex);
        }
    }
    createNewSessionData() {
        try {
            sessionDataLoader.ClearAlmeSessionData();
            this.almeSession = sessionDataLoader.GetAlmeSessionData();
        }
        catch (ex) {
            this.logger.error(ex);
        }
    }
    validateAlmeSessionData(response) {
        let almeResponse;
        if (Array.isArray(response) && !!response.length) {
            almeResponse = response[response.length - 1];
        }
        else {
            almeResponse = response;
        }
        if (almeResponse && almeResponse.userId && this.almeSession.userId !== almeResponse.userId) {
            const almeSessionData = {
                sessionId: this.almeSession.sessionId,
                userId: almeResponse.userId,
                lastActivityTime: null,
            };
            try {
                sessionDataLoader.UpdateAlmeSessionData(almeSessionData);
            }
            catch (ex) {
                this.logger.error(ex);
            }
            this.almeSession = almeSessionData;
        }
    }
    appendToHistory(response) {
        var _a;
        if (!response || !response.userId) {
            return;
        }
        const isLiveChatConnected = ((_a = response === null || response === void 0 ? void 0 : response.extensionData) === null || _a === void 0 ? void 0 : _a.MessageSource) === 'LiveChat';
        const entryExtensionData = response.extensionData;
        this.almeState.history.push(response);
        this.setAlmeState(Object.assign(Object.assign({}, this.almeState), { lastEntryId: response.extensionData.entryId || undefined, chatId: entryExtensionData.chatId || this.almeState.chatId, didSessionEndAsNotEngaged: response.didSessionEndAsNotEngaged, lastActivityTime: Date.now(), isLiveChatConnected }));
    }
    async getLiveChatNotifications() {
        try {
            const request = new LiveChatNotificationRequestDetailsNext(this.almeSession.sessionId);
            const response = await this.almeApi.GetPendingMessagesAsync(request);
            if (response) {
                this.appendLiveChatToHistory(response);
            }
        }
        catch (error) {
            this.logger.error(error);
        }
    }
    appendLiveChatToHistory(response) {
        if (!response || !response.length) {
            return;
        }
        updateOperatorNameInBeginLiveChatResponse(response, this.almeState.history);
        updateOperatorNameInSessionEndResponse(response, this.almeState.history);
        updateNameAsNotEngagedInSessionEndResponse(response, this.almeState.history, this.almeState);
        updateSessionEndResponseWhenTimeout(response, this.almeState);
        this.almeState.history.push(...response);
        const lastResponse = response[response.length - 1];
        this.setAlmeState(Object.assign(Object.assign({}, this.almeState), { isLiveChatConnected: true, lastActivityTime: Date.now(), lastEntryId: lastResponse.extensionData.entryId, didSessionEndAsNotEngaged: lastResponse.didSessionEndAsNotEngaged }));
    }
    isConsumerCenterLink(url) {
        return this.appConfig.main.platform === "PSCC" && ConsumerCenterNavigationLinkRegex.test(url);
    }
    urlNavigation(url, navigationType, appConfig) {
        if (url && this.isConsumerCenterLink(url)) {
            this.requestConsumerCenterNavigation(url, appConfig);
        }
        else {
            this.minimizeInMobileView();
            window.open(url, navigationType);
        }
    }
    requestConsumerCenterNavigation(url, appConfig) {
        const re = /^\{(.*)\}$/;
        const regexarray = re.exec(url);
        const navKey = regexarray ? regexarray[1] : null;
        const userMetadata = appConfig.main.userMetadata;
        if (navKey) {
            postMessage({
                type: MessageType.DeepLinkReqCCTR,
                accessKey: this.almeState.accessKey,
                source: 'nit',
                sessionInfo: { sessionId: appConfig.main.clientSessionId },
                targetPageKey: navKey,
                userData: { accountId: getAccountId(this.almeState, appConfig.main.clientSessionId, userMetadata === null || userMetadata === void 0 ? void 0 : userMetadata.accountId) },
            });
        }
    }
    minimizeInMobileView() {
        const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        if (this.appConfig.mobileBreakpoint && (windowWidth <= this.appConfig.mobileBreakpoint)) {
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            setTimeout(() => this.almeMinimizeUtils.onMinimizeClick());
        }
    }
    async endConversation() {
        try {
            const sessionId = this.almeSession.sessionId;
            const request = new EndConversationRequestDetailsNext(sessionId);
            await this.submitReportingEvent('EndConversation', `User conversation ending, session Id: ${sessionId}.`);
            const conversationEnded = await this.almeApi.EndConversationAsync(request);
            if (!conversationEnded) {
                this.logger.error(`Conversation not ended for session Id: ${sessionId}`);
            }
        }
        catch (error) {
            this.logger.error(error);
        }
    }
    async TransmitWebLogger(eventName, origin, accessKey, parameters = {}) {
        try {
            const apihostname = this.appConfig.main.apiHostName;
            const url = getWebLogAPIUrl(apihostname);
            const appEventRequest = new AppEventRequestDetailsNext(this.almeSession.userId, this.almeSession.sessionId, eventName, origin, parameters, this.appConfig.channel, undefined, undefined, accessKey);
            const options = {
                method: "POST",
                body: JSON.stringify(appEventRequest),
            };
            await fetch(url, options);
        }
        catch (error) {
            this.logger.error(error);
        }
    }
}
